import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ObjectIdentifier} from "../../../../../../../../core/shared/basic/object-identifier";
import {
    BmCamtImportDocumentTypeQlService,
    CamtImportDocumentTypeItem
} from '../../../../../../service/ql/bm-camt-import-document-type-ql.service';

/**
 * CAMT upload for a creditor.
 *
 * Relocated from the bank-account OBC: the upload now lives at creditor level. It binds
 * mvs-dm-upload to an ObjectIdentifier('bm.Creditor', creditorId) so uploaded statement files are
 * attached to the creditor and derives the upload-dropdown state from the creditor's ACTIVE
 * CAMT-import config(s).
 */
@Component({
    selector: 'bm-creditor-camt-upload',
    templateUrl: 'bm-creditor-camt-upload.component.html',
    standalone: false
})
export class BmCreditorCamtUploadComponent implements OnInit, OnChanges, OnDestroy {

    initialized: boolean;

    @Input() creditorId: number;

    objectIdentifier: ObjectIdentifier;
    uploadAccept = 'application/pdf,.xml,application/xml,text/xml,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel,.csv,text/csv';

    /**
     * Guards the <mvs-dm-upload> mount: only rendered once the importable-document-type
     * query has returned, so the upload component mounts with the correct inputs already
     * set (no late-arriving-input reaction needed).
     */
    configTypesLoaded: boolean = false;

    /** Importable DmDocumentTypes derived from the creditor's ACTIVE CAMT-import config(s). */
    importDocumentTypes: CamtImportDocumentTypeItem[] = [];

    /**
     * Fixed document type id — bound to [documentTypeId]. In the single-type case this fixes
     * the type; in the multi-type case it serves as the default preselection.
     */
    defaultDocumentTypeId: number | null = null;

    /** All importable type ids — bound to [allowedDocumentTypeIds] in the multi-type case. */
    allowedDocumentTypeIds: number[] = [];

    /** true → user picks in a (restricted or full) dropdown; false → type is fixed. */
    documentTypeSelectionMode: boolean = true;

    /** true → dropdown visible; false → hidden (fixed single-type case). */
    showDocumentTypeDropdown: boolean = true;

    /** true → no active CAMT-import document type configured for this creditor (fallback shown). */
    noConfiguredType: boolean = false;

    constructor(
        protected camtImportDocumentTypeService: BmCamtImportDocumentTypeQlService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent() {
        this.refreshComponent();
    }

    refreshComponent() {

        if (!this.creditorId) {
            return;
        }

        this.objectIdentifier = new ObjectIdentifier('bm.Creditor', this.creditorId);

        this.loadImportDocumentTypes();

        this.initialized = true;
    }

    /**
     * Loads the importable DmDocumentTypes for the creditor's ACTIVE CAMT-import config(s)
     * and derives the upload-dropdown state. The <mvs-dm-upload> mount is gated on
     * configTypesLoaded so it renders only after this query returns.
     */
    private loadImportDocumentTypes() {

        this.configTypesLoaded = false;

        this.camtImportDocumentTypeService.getCamtImportDocumentTypesForCreditor(this.creditorId).subscribe({
            next: (types) => {
                this.importDocumentTypes = types ?? [];
                this.deriveUploadState(this.importDocumentTypes);
                this.configTypesLoaded = true;
            },
            error: () => {
                // On query failure fall back to the previous behavior (full dropdown, no default).
                this.importDocumentTypes = [];
                this.deriveUploadState([]);
                this.configTypesLoaded = true;
            }
        });
    }

    /**
     * Derives the <mvs-dm-upload> input state from the importable document types:
     *  - exactly one   → fix the type (dropdown hidden, selection off, documentTypeId set).
     *  - more than one → restricted dropdown (allow-list) with the first type as default.
     *  - none          → fallback to the full, unrestricted dropdown + a German hint.
     */
    private deriveUploadState(types: CamtImportDocumentTypeItem[]) {

        if (types.length === 1) {
            // Single importable type — fix it and hide the dropdown.
            this.defaultDocumentTypeId = types[0].id;
            this.allowedDocumentTypeIds = [];
            this.documentTypeSelectionMode = false;
            this.showDocumentTypeDropdown = false;
            this.noConfiguredType = false;
        } else if (types.length > 1) {
            // Several importable types — restricted dropdown with a default preselection.
            this.defaultDocumentTypeId = types[0].id;
            this.allowedDocumentTypeIds = types.map(t => t.id);
            this.documentTypeSelectionMode = true;
            this.showDocumentTypeDropdown = true;
            this.noConfiguredType = false;
        } else {
            // No active config / no importable type — fall back to the previous full dropdown.
            this.defaultDocumentTypeId = null;
            this.allowedDocumentTypeIds = [];
            this.documentTypeSelectionMode = true;
            this.showDocumentTypeDropdown = true;
            this.noConfiguredType = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["creditorId"]) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }
}
